import {
  DateHelper,
  DomClassList, EventHelper,
  Mask,
  MessageDialog,
  Popup,
  StringHelper,
  Toast,
  Widget
} from "@bryntum/schedulerpro";
import { createRef } from "react";
import { toast } from "react-toastify";
import { PERSISTENCE_STATE_KEYS } from "../constants/PersistenceStateKeys.jsx";
import { EDITOR_DATA_TAG, EDITOR_KEYS } from "../constants/EditorKeys.jsx";
import { GRANTS } from "../constants/PrivilegeTypes.jsx";
import {
  APP_HEADER_HOVER_MENU_BUTTON_CLS,
  APP_HEADER_ID,
  ASYNC_TASK_TYPES,
  DynamicParamKey, ICON_COL_DEF_WIDTH,
  NO_VALUE,
  nonSupportedViewsForViewState,
  REMINDER_ICONS,
  REMINDER_STATE,
  SOCKET_CHANGES_UPDATE_NOTIFICATION, SWIVU_ASSIST_COLUMN_MAP,
  SWIVU_GROUP_CONTEXT_MENU_SELECTOR,
  ENTITY_TYPES
} from "../constants/OtherKeys.jsx";
import { SWIVU_STANDARD_ICONS } from "../constants/IconKeys.jsx";
import { eventStyles, STRIPED_TASK_STYLE } from "../constants/TaskTypes.jsx";
import { TAGS_KEYS } from "../constants/TagsKeys.jsx";
import { createAgendaUrl } from "../../env.jsx";
import {
  getFontSize,
  userPreferences
} from "../../react/context/bryntumContext.jsx";
import SwivuCalendarHelper from "./calendarUtils.jsx";
import {
  swivuAlertBellActiveCls,
  swivuAlertBellWarnCls
} from "../configs/globalConfig.jsx";
import isEqual from "lodash.isequal";
// import Linkify from "react-linkify";
import EditorBase from "../../bryntum-lib/editors/base/EditorBase.jsx";
import { addTimelineZoom, debounce } from "./otherUtils.jsx";
import { pluralize } from "./otherUtils.jsx";
import { logErrorToSentry } from "./projectUtils.jsx";
import { CheckSquare, Users, Building2, UserCircle, Link2 } from "lucide-react";


export const defaultCellRenderer = ({ value }) => value || "N/A";

/**
 * @return {{startDate: string|Date, endDate: string|Date}}
 * */
export const getDatesFromSelectedEventTick = (menu) => {
  const { owner } = menu;
  const { point } = owner.features.timeAxisHeaderMenu.menuContext;
  const clickedDate = owner.getDateFromCoordinate(point[0], "floor", false);
  const tick = owner.timeAxis.find(record => record.startDate <= clickedDate && record.endDate > clickedDate);
  return tick;
};

/**
 * @param {{[key: string]: boolean}} styleObj
 * @return {string}
 * */
export const clsx = (styleObj) => (new DomClassList(styleObj)).trim();

/**
 * @param {{ html?: string, text?: string }} params
 * */
export const getDivider = ({ html, text, cls, ...rest } = {}) => ({
  flex: "1 0 100%",
  html: html || `<div class="b-divider ${cls || ''}" style="flex: 1 0 100%;" data-text="${text}" />`,
  ...rest
});

export const setNonWorkingDaysFromState = (project) => {
  const boardData = StringHelper.safeJsonParse(userPreferences.getSetting(PERSISTENCE_STATE_KEYS.boardSetting, null, true));
  if (boardData?.nonWorkingSlots) {
    SwivuCalendarHelper.addNonWorkingSlotsOnWeek(project, boardData.nonWorkingSlots);
  } else {
    const defaultNonWorkingSlots = getDefaultNonWorkingSlots();
    userPreferences.updateSetting(PERSISTENCE_STATE_KEYS.boardSetting, JSON.stringify(getDefaultNonWorkingSlots()), true);
    SwivuCalendarHelper.addNonWorkingSlotsOnWeek(project, defaultNonWorkingSlots.nonWorkingSlots);
  }
};

export const getDefaultNonWorkingSlots = () => ({
  nonWorkingSlots: [
    {
      id: 0,
      day: "Sunday",
      allDay: true,
      begin: "00:00",
      end: "23:59"
    },
    {
      id: 1,
      day: "Saturday",
      allDay: true,
      begin: "00:00",
      end: "23:59"
    }
  ]
});

// TODO: not being used anywhere and can be removed, but keeping it here for reference
/**
 * selects all opened editors from DOM and hide all
 * @param {() => {}} onEditorsClosed
 * */
export const closeAllOpenedEditors = async (onEditorsClosed) => {
  const editorRefs = [...document.querySelectorAll(`[${EDITOR_DATA_TAG.value}]`)];
  for (const editorRef of editorRefs) {
    const editor = Widget.getById(editorRef.id);
    // we've to await otherwise, sometimes if multiple editors are opened then some gets closed but other
    await editor.hide();
  }
  onEditorsClosed();
};

/**
 * create widget with the default params set
 * @param {{ type: string }} params
 * */
export const createWidget = (params) => {
  return Widget.create({
    autoShow: false,
    centered: true,
    dataset: {
      [EDITOR_DATA_TAG.key]: ""
    },
    ...params
  });
};

// TODO: replicate this at all other places

/**
 * @typedef {{ref?: string, onClick: () => {}}} ButtonConfigs
 */
/**
 * @param {ButtonConfigs} addButton
 * @param {ButtonConfigs} deleteButton
 * @return Object
 */
export const defaultAddDeleteButton = ({ addButton, deleteButton }) => {
  const refs = {};

  if (addButton) {
    const { ref: addButtonRef, ...restAddButton } = { ref: "addButton", ...addButton };
    refs[addButtonRef] = {
      type: "button",
      icon: "fa fa-plus",
      ...restAddButton
    }
  }

  if (deleteButton) {
    const { ref: deleteButtonRef, ...restDeleteButton } = { ref: "deleteButton", ...deleteButton };
    refs[deleteButtonRef] = {
      type: "button",
      color: "b-red",
      icon: "fa fa-trash",
      ...restDeleteButton
    }
  }

  return refs;
};

/**
 * @param {string|Date[]} dates
 * @return {{minDate: Date, maxDate: Date}}
 */
export const getMinAndMaxDateFromDates = (dates) => {
  const minDate = new Date(Math.min(...dates));
  const maxDate = new Date(Math.max(...dates));
  return { minDate, maxDate };
};

// TODO: later replace all other places
export const showToast = ({ msg, type, duration }) => {
  const getColorByType = () => {
    if (type === "success") return "b-green";
    if (type === "error") return "b-red";
    if (type === "warn") return "b-deep-orange";
    return "b-red";
  };

  const getTimeout = () => {
    if (duration === "long") return 10000;
    if (duration === "medium") return 6000;
    else return 3000;
  }

  Toast.show({
    html: `<p class="feedback-savedmsg">${msg}</p>`,
    timeout: getTimeout(),
    style: "z-index: 2147483647",
    color: getColorByType(),
    rootElement: document.body
  });
};

// TODO: later replace all others places
/**
 * @param {{ confirmBtnText: !string=, cancelBtnText: !string=, title: string, message: string, isCloseByHeader: boolean }} params
 * @return Promise<boolean> - return true if user clicks okButton
 */
export const showConfirmationDialog = async ({
  confirmBtnText = "OK", cancelBtnText = "Cancel", title, message = "", isCancelButton = true, isCloseByHeader = false
}) => {
  MessageDialog.widgetMap.close.hidden = !isCloseByHeader;
  MessageDialog.widgetMap.cancelButton.hidden = !isCancelButton;
  const userResponse = await MessageDialog.confirm({
    okButton: confirmBtnText,
    cancelButton: cancelBtnText,
    title: title,
    message: message
  });
  MessageDialog.widgetMap.close.hidden = true;
  MessageDialog.widgetMap.cancelButton.hidden = false
  return userResponse === MessageDialog.okButton;
};


export const showShareUrlOptions = (editorParams) => {
  const popupInstance = new Popup({
    title: "Share",
    closable: true,
    closeAction: "destroy",
    modal: true,
    html: "How would you like to share?",
    bbar: {
      contentElementCls: "!px-[1em] !gap-[1em]",
      items: {
        confirmBtnText: {
          type: "button",
          text: "URL only",
          cls: "order-2 b-raised",
          color: "b-blue",
          onClick() {
            copyTextToClipboard(editorParams.linkOnly, null)
            popupInstance.close();
          }
        },
        cancelBtnText: {
          type: "button",
          text: "Description with URL",
          cls: "order-1 b-raised",
          color: "b-blue",
          onClick() {
            copyTextToClipboard(editorParams.linkWithDescription, null);
            popupInstance.close();
          }
        }
      }
    }
  });
  popupInstance.show();
}

/**
 * @description - This function takes data as param and initiate download in browser
 * @param {any} data - data which should be downloaded as a file
 * @param {string} fileName - name of the file to download
 * @param {string} extension - file extension
 */
export const downloadFileFromData = (data, fileName) => {
  // create file link in browser's memory
  const href = URL.createObjectURL(data);

  // create "a" HTML element with href to file & click
  const link = document.createElement('a');
  link.href = href;
  link.setAttribute('download', `${fileName}`);
  document.body.appendChild(link);
  link.click();

  // clean up "a" element & remove ObjectURL
  document.body.removeChild(link);
  URL.revokeObjectURL(href);
}

export const serverErrorMsg = () => (
  `<h3>Failed!</h3><p class="feedback-savedmsg">Something went wrong.</p>`
)

/**
 * @description - popupService inhibit unlimited popups creation issue by keeping all the popups
 * creation in app in a ref cache, this is similar to singleton approach, any popup creation in app should
 * be created via popupService.getPopup function
 * @type {{getPopup: (function(*, *): *), popupRef: React.RefObject<unknown>}}
 */
export const popupService = {
  popupRef: createRef(),
  getPopup: (popupId, popupConfigs) => {
    popupService.popupRef.current = popupService.popupRef.current || {};
    if (!popupService.popupRef.current[popupId]) {
      popupService.popupRef.current[popupId] = new Popup(popupConfigs);
    }
    return popupService.popupRef.current[popupId];
  },
}

export const defaultGroupRenderer = (e) => {
  const { groupRowFor: value, count, grid } = e;
  let renderValue;
  if (value && value !== NO_VALUE) renderValue = `${defaultCellRenderer({ value })}&nbsp;(${count})`;
  else renderValue = `${defaultCellRenderer({ value: null })} (${count})`;

  // not all grids in app want to have the context menu button on group header, such as editor's grid
  const isShowContextMenuBtn = grid?.features?.cellMenu?.clickTriggerSelector === SWIVU_GROUP_CONTEXT_MENU_SELECTOR;

  // group header context menu button
  return {
    tag: "div",
    class: "flex w-full justify-between items-center px-1",
    html: `<div class="flex items-center gap-2.5">
             <!-- context menu button display on group header hover -->
             ${isShowContextMenuBtn ? `<div class="relative leading-none cursor-pointer p-0 text-xl min-h-[auto] invisible ${SWIVU_GROUP_CONTEXT_MENU_SELECTOR.replace(".", "")} fa-solid fa-ellipsis"></div>`: ""}
             ${renderValue}
           </div>`
  }
}

export const getCustomGroupRenderer = (
  event, key
) => {
  const {groupRowFor: record, count, ...rest} = event;

  // when we open a cell menu, we don't have any clue for which column it had applied a group for.
  // our requirement is to display a setting button (which when clicked bring us to setting editor) in group menu, if the column on which grouping is applied is a tag column.
  event.record.swivuGroupColumn = event.groupColumn;

  if (record) {
    if (typeof record === "string") {
      if (record !== NO_VALUE) return defaultGroupRenderer({ groupRowFor: record, count, ...rest });
    } else if (Array.isArray(record) && record.length) {
      let keys = key ? record.map(rec => rec[key]) : record
      return defaultGroupRenderer({ groupRowFor: keys.join(", "), count, ...rest });
    } else if (typeof record === 'object' && !Array.isArray(record)) {
      let keys = key ? record[key] : record
      return defaultGroupRenderer({ groupRowFor: keys, count, ...rest })
    }
  }
  return defaultGroupRenderer({ groupRowFor: null, count, ...rest });
};

export const progressBar = (text= "This task is in progress", ref ) => {
  const progress = Mask.mask({
    text,
    progress: 0,
    maxProgress: 100
  }, ref);
  progress.progressElement.style.backgroundColor = "#CDCECF";
  return progress;
};

export const capitalizeText = (text) => (text.charAt(0).toUpperCase() + text.slice(1));

export const renderTaskSyleList = (item) => (
  `<div class="b-sch-event-wrap b-sch-style-${item.value} b-sch-custom-color">
    <div class="b-sch-event b-sch-has-content justify-center" style="color: ${item.value === 'mixed' || item.value === STRIPED_TASK_STYLE ? '#FFF' : '#FF8A65'};${item.value === STRIPED_TASK_STYLE ? applyStripedBackground('#FF8A65', true) : ''}">
      <div class="m-0 b-sch-event-content ${item.value === "line" || item.value === 'dashed' || item.value === "minimal" ? 'theme-text-color' : ''}">${capitalizeText(item.value)}</div>
    </div>
  </div>`
)

export const swivuCheckGridGrants = (grid, user) => {
  if (user) {
    const canMutate = user.can(GRANTS.grant_three);
    const canEdit = user.can(GRANTS.grant_four);

    // scheduler
    if (grid.isSchedulerPro) {
      const { eventMenu, scheduleMenu, eventDrag, eventResize, timeAxisHeaderMenu, eventDragCreate } = grid.features;
      if (eventMenu) eventMenu.disabled = !canMutate;
      if (scheduleMenu) scheduleMenu.disabled = !canMutate;
      if (eventDrag) eventDrag.disabled = !canMutate;
      if (eventResize) eventResize.disabled = !canMutate;
      if (timeAxisHeaderMenu.items.pushEvent) timeAxisHeaderMenu.items.pushEvent.disabled = !canMutate;
      if (eventDragCreate) eventDragCreate.disabled = !canMutate
      grid.enableDeleteKey = canMutate;
    }

    const { rowReorder, cellEdit, cellMenu } = grid.features;

    // create entity from grid column header menu
    grid.columns.forEach((col) => {
      if (col.headerMenuItems?.addNewEntity) {
        col.headerMenuItems.addNewEntity.disabled = !canEdit;
      }
    });
    // row reorder
    // For Grid
    if (rowReorder && (grid.ref !== "checklistItemGrid" && grid.ref !== "checklistGrid")) {
      // Temporarily disabling row reorder in main grids until we get a fix/patch for this issue: https://github.com/bryntum/support/issues/8263
      // Todo: Fix needed as uncommenting the showGrip statement causes the subGrid not persisting it resizing
      //       Happening on the certain grids such as Clients and Schedule Task grid
      // rowReorder.showGrip = canEdit;
      // rowReorder.disabled = !canEdit;

      rowReorder.showGrip = false;
      rowReorder.disabled = true;
    }
    // cell edit
    cellEdit.disabled = !canEdit;
    // cell menu items
    const { removeRow, archive, editEntity, sendMessage } = cellMenu.items;
    if (removeRow) removeRow.disabled = !canMutate;
    if (archive) archive.disabled = !canMutate;
    if (editEntity) editEntity.disabled = !canMutate
    if (sendMessage) sendMessage.disabled = !canMutate
  }
}

/**
 * @desc - Returns search param from URL by parameter id
 * @param {string} paramId
 * @return {string|null}
 */
export const getSearchParam = (paramId) => {
  const searchString = window.location.search;
  if (searchString) return new URLSearchParams(searchString).get(paramId);
  return null;
}

export const getDefaultDateFormat = (date) => DateHelper.format(new Date(date), "YYYY-MM-DD");

/**
 * @desc - Deletes archived records
 * @param {Object} store
 * @param {Object[]} selectedRecords
 */
export const removeArchivedRecords = (store, selectedRecords) => {
  if(store.modelClass.getFieldDefinition("archived")) {
    const records = selectedRecords.filter((record) => record.archived);
    if (records.length) {
      store.remove(records);
    }
  }
}

export const swiuvNotifyArchiveStateChangeOnStores = (stores, isDisplayArchiveRecords) => {
  stores.forEach((store) => {
    // notifying all stores, that extend SwivuStoreArchiveMixin class about archive state change
    if (store.store.isSwivuStoreArchiveMixin) store.store.onSwivuArchiveStateChange(isDisplayArchiveRecords);
  })
}

/**
 * @description - open agenda link in browser new tab
 */
export const openAgendaView = () => {
  const agendaURl = createAgendaUrl();
  window.open(`${agendaURl}/hq`, "_blank")
}

export const eventTooltipRenderer = (name, startText, endText, resources) => (
  `<div class="swivu-plan-event-tip">
    <p>${name}</p>
    <p class="b-icon b-icon-calendar">${startText}</p>
    <p class="b-icon b-icon-calendar">${endText}</p>
    ${resources.length ? (
    `
      <p>Resources</p>
      <div class="resource-container">
        ${resources.map(EntityMainNameDefaultString).join('')}
      </div>
    `
    ) : ''}
  </div>`
);

export const applyStripedBackground = (color, isInlineHTMLStyle = false) => {
  let bgStyle = `repeating-linear-gradient(-45deg, ${color} 0 15px, #eeeeee 15px 17px)`
  if (isInlineHTMLStyle) bgStyle = 'background: ' + bgStyle;
  return bgStyle;
}

/**
 * @description - return bell icon with configured settings according to provided params
 */
export const getAlertBell = ({ reminderState, cls = "reminder-alert-bell-icon", isAlertBell, isCancelIcon = false }) => {
  if (isAlertBell) {
    switch (reminderState) {
      case REMINDER_STATE.NO_REMINDER: cls = `${cls} ${REMINDER_ICONS.NO_REMINDER}`; break;
      case REMINDER_STATE.NOT_SENT: cls = `${cls} ${REMINDER_ICONS.NOT_SENT} text-[#0a88e9]`; break;
      case REMINDER_STATE.NOT_SEEN: cls = `${cls} ${REMINDER_ICONS.NOT_SEEN} text-[#dc7f00]`; break;
      case REMINDER_STATE.SEEN: cls = `${cls} ${REMINDER_ICONS.NO_REMINDER}`; break;
      case REMINDER_STATE.FAILED: cls = `${cls} ${REMINDER_ICONS.NO_REMINDER} ${swivuAlertBellWarnCls}`; break;
      default: cls = `${cls} ${REMINDER_ICONS.NO_REMINDER}`; break;
    }
  } else if (isCancelIcon) {
    if (reminderState === REMINDER_STATE.NOT_SEEN) cls = `${cls} fa-solid fa-xmark ${swivuAlertBellActiveCls}`
    cls = `fa-light fa-eye ${cls}`
  } else {
    if (reminderState === REMINDER_STATE.NOT_SEEN) cls = `${cls} ${swivuAlertBellActiveCls}`;
    else if (reminderState === REMINDER_STATE.FAILED) cls = `${cls} ${swivuAlertBellWarnCls}`;
    cls = `fa-light fa-eye ${cls}`
  }
  return `<i class='${cls}'></i>`;
}

export const convertImageToBase64 = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);

      const dataURL = canvas.toDataURL('image/png'); // or 'image/jpeg' for JPEG images
      resolve(dataURL);
    };

    img.onerror = reject;
    img.src = url;
  });
}

export const getFormattedPhotoItems = (photos) => (photos.map((photo) => ({ id: photo.id, uri: photo.uris[1].uri })))

export const getAging = (createdAt) => {
  if(!createdAt) return 0
  return parseFloat(DateHelper.diff(new Date(createdAt), new Date(), 'd').toFixed(1))
};
export const combineDateRanges = (dateRanges = []) => {
  if (!dateRanges.length) return [];
  const combinedRanges = [];
  let currentRange = { ...dateRanges[0] };
  for (let i = 1; i < dateRanges.length; i++) {
    const nextRange = dateRanges[i];

    if (nextRange.startDate <= currentRange.endDate) {
      // Extend the current range
      currentRange.endDate = DateHelper.max(currentRange.endDate, nextRange.endDate);
    } else {
      // Add the current range to the result and update the current range
      combinedRanges.push(currentRange);
      currentRange = { ...nextRange }; // Make a copy of the next range
    }
  }
  // Add the last range to the result
  combinedRanges.push(currentRange);
  return combinedRanges;
}

export const calculateDateRanges = (record, amount, unit, nonWorkingDays) => {
  let workingDateRanges = [];
  let nonWorkingDateRanges = [];
  let startDate = DateHelper.add(record.startDate, amount, unit);
  let endDate = DateHelper.add(record.endDate, amount, unit);
  record.shift(unit, amount);
  while (startDate < endDate) {
    let obj = {};
    const newDate = DateHelper.set(DateHelper.add(startDate, 1, "day"), {hour: 0, minute: 0, second: 0});
    const isNonWorking = nonWorkingDays.includes(DateHelper.format(startDate, 'ddd'));
    if (newDate < endDate) obj = {startDate: startDate, endDate: newDate, isNonWorking};
    else obj = {startDate: startDate, endDate, isNonWorking};

    if (isNonWorking) nonWorkingDateRanges.push(obj);
    else workingDateRanges.push(obj);

    startDate = newDate;
  }
  let appendNonWorkingFrom = null;
  if (workingDateRanges.length) appendNonWorkingFrom = DateHelper.clone(workingDateRanges[workingDateRanges.length - 1].endDate);
  workingDateRanges = workingDateRanges.concat(nonWorkingDateRanges.map((dateRange) => {
    if (appendNonWorkingFrom) {
      let duration = DateHelper.diff(dateRange.startDate, dateRange.endDate);
      dateRange.startDate = appendNonWorkingFrom;
      dateRange.endDate = DateHelper.add(appendNonWorkingFrom, duration)
      dateRange.isNonWorking = nonWorkingDays.includes(DateHelper.format(appendNonWorkingFrom, "ddd"))
    }
    while (dateRange.isNonWorking) {
      dateRange.startDate = DateHelper.add(dateRange.startDate, 1, "day");
      dateRange.endDate = DateHelper.add(dateRange.endDate, 1, "day");
      dateRange.isNonWorking = nonWorkingDays.includes(DateHelper.format(dateRange.startDate, "ddd"))
    }
    appendNonWorkingFrom = DateHelper.clone(dateRange.endDate);
    return dateRange;
  }));
  return combineDateRanges(workingDateRanges);
}

export const getFontAwesomeClassName = (url) => {
  try {
    const iconUrl = new URL(url);

    const iconName = iconUrl.href.split("?")[0].split("/").at(-1);
    const iconType = iconUrl.searchParams.get("f");
    const style = iconUrl.searchParams.get("s");

    let className = `fa-${iconName}`;

    if (iconType) {
      className += ` fa-${iconType}`;
    }

    if (style) {
      className += ` fa-${style}`;
    }

    return className;
  } catch {
    return "fa-" + url;
  }
}

export const getViewStateIconByRecord = (vsRecord, project) => {
  if (vsRecord) {
    const vsIconId = vsRecord.iconId;
    if (vsIconId) {
      const iconById = project.resourceTagValues.find((r) => r.tagSet === TAGS_KEYS.icon && r.id === vsIconId);
      if (iconById) return `fa ${getFontAwesomeClassName(iconById.tagValue)}`;
    } else if (vsRecord.isDefaultView && SWIVU_STANDARD_ICONS[vsRecord.viewType]) {
      return SWIVU_STANDARD_ICONS[vsRecord.viewType];
    }
  }

  return SWIVU_STANDARD_ICONS.defaultView;
}

export const getURLWithBoardSlug = (url, boardSlug) => {
  if (url.includes(`/:${DynamicParamKey}`)) {
    const newRoute = url.replace(`/:${DynamicParamKey}`, `/${boardSlug}`);
    return `${newRoute}${window.location.search}`;
  }
  return url;
}

export const removeEscapeSequences = (str) => str
  .replace(/\\[tn]/g, ' ') // Replace \t and \n with a space
  .replace(/\\'|\\"/g, match => match.slice(1)) // Replace \' with ' and \" with "
  .replace(/\\./g, ''); // Remove other escape sequences

export const copyTextToClipboard = (text, msg = "Link copied to clipboard") => {
  navigator.clipboard.writeText(text);
  if (msg) {
    toast.success(msg);
  }
}

export const transformNonWorkingData = (records) => {
  return records.reduce((previousValue, currentItem) => {
    if (currentItem.allDay) {
      return previousValue.nonWorkingDays.includes(currentItem.dayName) ? previousValue : {...previousValue, nonWorkingDays: [...previousValue.nonWorkingDays, currentItem.dayName]}
    } else {
      if (previousValue.nonWorkingTimes[currentItem.dayName]) {
        previousValue.nonWorkingTimes[currentItem.dayName].push({startTime: currentItem.begin, endTime: currentItem.end})
      }
      else {
        previousValue = {
          ...previousValue,
          nonWorkingTimes: {
            ...previousValue.nonWorkingTimes,
            [currentItem.dayName]: [{startTime: currentItem.begin, endTime: currentItem.end}]
          }
        }
      }
      return previousValue
    }
  }, {nonWorkingDays: [], nonWorkingTimes: {}});
}

export const filterOutRangesInDay = (rangesToFilter, startDate, endDate = DateHelper.endOf(startDate)) => {
  const DATE_FORMAT = "ddd MMM DD YYYY";
  const TIME_FORMAT = "HH:mm";
  const filteredRanges = [];
  let currentNonWorkingTimeIndex = 0
  while (startDate < endDate) {
    if (!rangesToFilter[currentNonWorkingTimeIndex]) {
      filteredRanges.push({ startDate: startDate, endDate })
      break;
    }
    const tickDateString = DateHelper.format(startDate, DATE_FORMAT);
    if (DateHelper.format(startDate, TIME_FORMAT) === rangesToFilter[currentNonWorkingTimeIndex].startTime) {
      startDate = DateHelper.parse(
        `${tickDateString} ${rangesToFilter[currentNonWorkingTimeIndex].endTime}`,
        `${DATE_FORMAT} ${TIME_FORMAT}`
      )
      currentNonWorkingTimeIndex++;
      continue;
    }
    filteredRanges.push({
      startDate: startDate,
      endDate: DateHelper.parse(
        `${tickDateString} ${rangesToFilter[currentNonWorkingTimeIndex].startTime}`,
        `${DATE_FORMAT} ${TIME_FORMAT}`
      )
    });
    startDate = DateHelper.parse(
      `${tickDateString} ${rangesToFilter[currentNonWorkingTimeIndex].endTime}`,
      `${DATE_FORMAT} ${TIME_FORMAT}`
    )
    currentNonWorkingTimeIndex++;
  }
  return filteredRanges;
}

export const lineThroughRenderer = (value, canApplyLineThrough) => ({
  text: defaultCellRenderer({ value }),
  style: canApplyLineThrough ? { textDecoration: 'line-through' } : {}
})

export const lineThroughString = (value, canApplyLineThrough) => {
  return `<span class="${canApplyLineThrough ? "line-through" : ""}">${value}</span>`
}

export const EntityMainNameDefault = (configs = {}) => ({
  tag: "div",
  class: `entity-main-name-column ${!configs.text ? "empty-entity-name" : ""} ${configs.containerClass ? configs.containerClass : ""}`,
  children: [{
    tag: "span",
    class: "entity-main-name-text",
    ...configs,
  }]
})

export function EntityMainNameDefaultString(resource) {
  return `<div class="entity-main-name-column">
    <span 
      ${resource.archived ? 'style="text-decoration: line-through"' : ""} 
      class="entity-main-name-text dark:text-[#cdcecf] text-[#222]">
        ${resource.name || "N/A"}
      </span>
  </div>`;
}

export function generateMultiFilterConfigFromModel(modelClass) {
  const fieldTypes = {};

  for (const fieldName in modelClass.fieldMap) {
    const { name, type, persist, internal } = modelClass.fieldMap[fieldName];

    fieldTypes[name] = { type : type === 'auto' ? 'string' : type, persist, internal };
  }

  return fieldTypes;
}

export const imageIconDefaultString = () => {
  return `<i class="fa-light fa-image image-icon-column"></i>`
}

export const imageIconRenderer = (canApplyStyle = false) => {
  return {
    tag: "i",
    class: "fa-light fa-image",
    style: canApplyStyle ? { color: "#26a6ff" } : {}
  }
}

export const imagesCountColumn = (field) => ({
  id: "image",
  field,
  text: "Image",
  width: 30,
  minWidth: ICON_COL_DEF_WIDTH,
  editor: false,
  searchable: false,
  align: "center",
  sealed: true,
  filterable: false,
  htmlEncode: false,
  cls: "swivu-icon-column",
  headerRenderer() {
    return imageIconDefaultString();
  },
  renderer({ value }) {
    return {
      tag: "span",
      text: value
    }
  }
});

export const commentIconDefaultString = () => {
  return `<i class="fa-light fa-comment comment-icon-column"></i>`
}

export const commentIconRenderer = (canApplyStyle = false) => {
  return {
    tag: "i",
    class: "fa-light fa-comment",
    style: canApplyStyle ? { color: "#26a6ff" }: {}
  }
}

export const commentsCounColumn = (field) => ({
  id: "comment",
  field,
  text: "Comment",
  width: 30,
  editor: false,
  searchable: false,
  minWidth: ICON_COL_DEF_WIDTH,
  align: "center",
  sealed: true,
  filterable: false,
  htmlEncode: false,
  cls: "swivu-icon-column",
  headerRenderer() {
    return commentIconDefaultString();
  },
  renderer({ value }) {
    return {
      tag: "span",
      text: value
    }
  }
});

export const getRedirectToAgendaIfCantView = async (isPermission, board) => {
  let isConfirmed = false;
  if (!isPermission) {
    isConfirmed = await showConfirmationDialog({
      title   : `Not Enough Permission on Board "${board.name}"`,
      message : `You must have at least "Office" role to view scheduler. Press OK to redirect you to Agenda view.`
    })
  }
  if (isConfirmed || window.innerWidth < 768) {
    const searchParams = new URLSearchParams(window.location.search);

    if (searchParams.has("entityType")) {
      const entityType = searchParams.get("entityType");
      const entityId = +searchParams.get("entityId");
      const params = `/hq/${entityType}/${entityId}`;
      const agendaURL = createAgendaUrl() + params;

      window.location.replace(agendaURL);

      return false;
    }
  }

  return true;
}

export const displayPermissionNotAllowed = (keyMsg, currentRole) => MessageDialog.alert({
  title: "Permission Denied!",
  message: `Your role is "${currentRole}", and You don't have permission to ${keyMsg} this record.`
});

export const canAddNewUser = (user) => {
  return (user && !user.can(GRANTS.grant_one)) || (user && user.isDemoUser && user.isSandboxBoard)
}

export const isObjDeepEqual = (obj1, obj2) => {
  return isEqual(obj1, obj2);
}
/**
* @description Height set in a Column renderer takes precedence over the height returned by this function.
  Return null to use grid configured row height.
* */
export function getGridRowHeight(record){
  // There was an issue where dragging event on cause scroll of timeline to shift up
  // This is a task => Investigate Flicker Issue On Schedule Timeline
  // https://admin.dev.swivu.com/swivu-engineering-rjv68i/scheduler?task=106591&tab=general
  // The problem was previously we were giving fixed height to rows while some some rows height of resource records can be
  // arbitrary if the row on timeline has many stack of events
  if (this && this.isSchedulerPro && this.isHorizontal && !record.isGroupHeader) {
    // Note: This code is copied from SchedulerBase file
    const height = this.currentOrientation.calculateRowHeight(record);
    this.rowManager.storeKnownHeight(record.id, height);
    return height;
  }
  return record.isGroupHeader ? getFontSize() * 2 : this.rowHeight;
}

export const getUserGrantsTextOnly = (roleGrants) => {
  return roleGrants.reduce((accum, grant) => {
    const grantDescription = grant.description;
    const separator = "\n";
    if (grantDescription.includes(separator)) {
      accum.push(...grantDescription.split(separator));
    } else {
      accum.push(grantDescription);
    }
    return accum;
  }, []).map((description) => ({ description }));
}

// this function runs first before preventRowDeselectOnSameSelectedRowClick function below
export const preventMultiselectOnCtrlKeyClick = (e) => {
  e.grid.swivuAllowDeselectRow = true;
  // if only ctrlKey check, then both keys (ctrl, command/window in mac/windows) are considered in this check
  if (e.event.ctrlKey && !e.event.metaKey) {
    if (!e.grid.isSelected(e.record)) {
      e.grid.deselectAll();
    } else {
      // currently, if clicking on the same row which is already selected and user attempts to select the same row again then the row gets deselected
      // we are preventing that with signal property swivuAllowDeselectRow that will be handled in preventRowDeselectOnSameSelectedRowClick function
      e.grid.swivuAllowDeselectRow = false;
    }
  } else if (e.event.metaKey && e.column.isTimeAxisColumn) {
    // if user attempts to click again a timeaxis cell then do not un-highlight the schedule resource grid row
    e.grid.swivuAllowDeselectRow = !e.grid.isSelected(e.record);
  }
}

export const preventRowDeselectOnSameSelectedRowClick = (e) => {
  return e.source.swivuAllowDeselectRow;
}

export const buildTaskEntityColorMenu = ({ item, record, selectedItems, onAfterChange }) => {
  item.menu[0].value = record.color;
  item.menu[0].onChange = (e) => {;
    selectedItems.forEach((selectedItem) => selectedItem.color = e.value);
    if (onAfterChange) onAfterChange(e);
  }
  return item;
}

export const buildTaskEntityStyleMenu = ({ item, record, selectedItems, onAfterChange }) => {
  item.menu[0].value = record.taskStyle || eventStyles[0];
  item.menu[0].items = [...eventStyles, STRIPED_TASK_STYLE];
  item.menu[0].onChange = (e) => {
    selectedItems.forEach((selectedItem) => selectedItem.taskStyle = e.value);
    if (onAfterChange) onAfterChange(e);
  }
  return item;
}

export const preventGroupToggleOnGroupHeaderBtn = (e) => {
  return !e?.domEvent?.target?.classList?.contains(SWIVU_GROUP_CONTEXT_MENU_SELECTOR.replace(".", ""));
}

export const getOriginalRecord = (record) => record?.isLinked ? record.$original : record;

export const loadUserAvatars = (widget, activeUsersData, project) => {
  widget.html = activeUsersData.reduce((prevState, userData) => {
    const userRecord = project.boardUsersStore.find(record => record.userId === userData.userId);
    if (userRecord) {
      if (userRecord.profileImageURL) {
        prevState += `<img class="dark:bg-[#353539] dark:text-white dark:border-0 border-solid border-2 border-[#fd9301] flex items-center justify-center w-12 h-12 rounded-full" src="${userRecord.profileImageURL}" alt="${userRecord.name}"/>`
      } else {
        const initials = (userRecord.name ?? "NA").split(" ").slice(0, 2).map(word => word[0].toUpperCase()).join("");
        prevState += `
        <div class="dark:bg-[#353539] dark:text-white dark:border-0 border-solid border-2 border-[#fd9301] flex items-center justify-center w-12 h-12 rounded-full">
          ${initials}
        </div>`;
      }
    }
    return prevState;
  }, "");
}

export const getFormattedBoardData = (boardData) => {
  return ({
    ...boardData,
    roles: boardData.roles?.[0],
  });
}
export function swivuGridSorterFn(a, b) {
  let fieldA = a[this.field];
  let fieldB = b[this.field];
  if (Array.isArray(fieldA)) {
    fieldA = fieldA.join(" ");
    fieldB = fieldB.join(" ");
  }
  if (!fieldA && !fieldB) {
    return 0;
  } else if (!fieldA) {
    return -1;
  } else if (!fieldB) {
    return 1;
  } else {
    return fieldA.localeCompare(fieldB, "en", { numeric: true });
  }
};

export const swivuGroupSortFn = ({ _groupValue: lhs }, { _groupValue: rhs }) => {
  if (!lhs && !rhs) {
    return 0;
  } else if (!lhs) {
    return -1;
  } else if (!rhs) {
    return 1;
  } else {
    if (typeof lhs === "string") {
      return lhs.localeCompare(rhs, "en", { numeric: true });
    } else {
      return lhs < rhs ? -1 : 1;
    }
  }
};

export const linkifyText = (value) => {
  return value?.text || value;
  // Todo: to find solution for now Linkify causes the issues with grid search feature added on header.
  // Make a search which should highlight value to description column and then clear the search field and navigating to different page... Oops happened
  /*return <Linkify
    componentDecorator={(decoratedHref, decoratedText, key) => (
      <a target="_blank" style={{color: "#048ded"}} href={decoratedHref} key={key}>
        {decoratedText}
      </a>
    )}
  >
    {value.text || value}
  </Linkify>*/
}

export const getNameInitials = (name) => {
  if(!name) return "N/A";
  const [firstWord, secondWord] = name.split(" ");
  return firstWord[0] + (secondWord?.[0] || "");
}

export const columnAvatarRenderer = ({
  image,
  name,
  archived = false,
  size,
  showName = true,
  showAvatar = true,
  containerConfig = {},
  otherConfig
}) => {
  let avatar;
  if (showAvatar) {
    const avatarSize = Math.max(20, size - 11);
    const getSize = () => {
      return {
        height: avatarSize,
        width: avatarSize,
        flexShrink: 0,
      }
    }
    if (image && image.length > 0) {
      avatar = {
        tag: 'img',
        class: `swivu-avatar swivu-avatar-tooltip`,
        style: getSize(),
        src: `${image}`,
        dataset: {
          name
        }
      }
    } else {
      avatar = {
        class: `swivu-avatar-initials swivu-avatar-tooltip swivu-icon`,
        text: getNameInitials(name),
        style: {...getSize(), padding: "10px", fontSize: avatarSize * 0.56 }, // Here using coeffecient 0.56 because that is the fontSize/height ratio of what google is using, To achieve similar styling we are keeping fontSize 56% of the avatar height
        dataset: {
          name
        }
      }
    }
  }

  const domConfig = {
    ...containerConfig,
    class: `entity-main-name-container ${!showName ? "!w-auto" : ""} ${containerConfig.class ? containerConfig.class : ""}`,
    children: []
  }
  if (avatar) domConfig.children.push(avatar);
  if (showName) domConfig.children.push(EntityMainNameDefault({
    ...lineThroughRenderer(name || "N/A", archived),
    containerClass: "truncate min-w-[5em]"
  }));
  if (otherConfig) domConfig.children.push(otherConfig);
  return domConfig;
}

export const columnAvatarConfig = {
  columnAvatarRenderer,
  tooltipRenderer: ({ cellElement }) => {
    return cellElement.dataset.name || "N/A"
  }
}

export const cellAvatarTooltipConfig = {
  forSelector : '.swivu-avatar-tooltip',
  hideDelay: 0,
  hoverDelay: 200
}

export const columnAvatarString = (name, image) => {
  const classes = `h-[2em] w-[2em] rounded-full flex-shrink-0`
  const avatar = image ? `<img class='swivu-avatar ${classes}' src="${image}">` : `<div class='swivu-avatar-initials ${classes}'>
    ${getNameInitials(name)}
  </div>`;
  return `<span class="swivu-avatar-tooltip flex items-center gap-[1em]" data-name="${name}">${avatar} ${name}</span>`
}

export const validateStartEndDateOfColumn = ({ editorContext, record, value }) => {
  const { column } = editorContext;
  if (column.field === "endDate") {
    if (DateHelper.isEqual(record.startDate, value) || DateHelper.isBefore(value, record.startDate)) {
      return "End Date cannot be exactly equals or greater to the Start Date";
    }
  } else if (column.field === "startDate") {
    if (DateHelper.isEqual(record.endDate, value) || DateHelper.isBefore(record.endDate, value)) {
      return "Start Date cannot be exactly equals or greater to the End Date";
    }
  }
  return true;
}

export const isIAmOneOfBoardOwner = (project, ownerId) => {
  const currentUser = project.boardUsersStore.swivuCurrentUser;
  const currentBoardUserRecord = project.boardUsersStore.find((user) => user.userId === currentUser.id);
  return currentBoardUserRecord?.id === ownerId;
}

export const showArchiveRecordAlert = (records) => {
  if (!records) throw new Error("Please provide a valid record instance");
  const archivedRecord = Array.isArray(records) ? records.find(record => record.archived) : (records.archived ? records : false);
  if (archivedRecord) {
    MessageDialog.alert({
      title: `${archivedRecord.entityVisibleName} is Archived`,
      message: `${archivedRecord.entityVisibleName} is archived and cannot be linked.`
    });
  }
  return archivedRecord;
};

export const handleGridDeleteButtonState = (grid, deleteButton, canDo = () => true) => {
  if (grid && deleteButton) {
    const setDeleteButtonState = (isDisabled) => deleteButton.disabled = isDisabled;

    /* Disable button on initial trigger if no records are selected */
    setDeleteButtonState(!(grid.selectedRecords?.length));
    return grid.on({
      selectionChange: ({ mode }) => {
        if (mode === "row" && grid.selectedRecord && canDo(grid.selectedRecord)) {
          setDeleteButtonState(!(grid.selectedRecords?.length));
        } else {
          setDeleteButtonState(true);
        }
      }
    })
  }
}

export const checkUserEmailAndRole = (project, authUser, boardUser, changes) => {
  const { email, role: roles } = changes;
  if (email && authUser.email === email.oldValue) {
    MessageDialog.alert({
      title: "Email Updated",
      message: `Your email has been updated to ${email.value}. Please log in with your new email.`
    }).then(() => {
      window.location.reload();
    })
    return false
  } else if (roles && authUser.id === boardUser.userId && roles.oldValue[0] !== roles.value[0]) {
    const roleRecord = project.swivuAppRoles.find((appRole) => appRole.id === roles.value[0]);
    if (boardUser.isDeactivated) {
      MessageDialog.alert({
        title: "Role Deactivated",
        message: `Your role is now ${roleRecord.name}. For support, please contact the administrator.`
      }).then(() => {
        window.location.reload();
      })
    } else {
      MessageDialog.alert({
        title: "Role Updated",
        message: `Your role is now ${roleRecord.name}. Press OK to reload the page and apply the changes.`
      }).then(() => {
        window.location.reload();
      })
    }
    return false;
  }
  return true;
};

export const deselectionOnHeaderClick = (cb) => {
  return EventHelper.on({
    element: document.querySelector(`#${APP_HEADER_ID}`),
    mousedown({ target }) {
      // When clicked on the view buttons in the app header triggers a state change(below logic) and updates the selected `viewState`.
      // However, the new `viewState` is not yet mapped to the widget's state yet.
      // This mismatch causes the app to compare the old and new view state, leading to the custom view being dismissed when selected.
      // Therefore, preventing the state change when clicked on view buttons
      if (cb && !target.closest(".b-button")) {
        const grids = cb();
        if (grids) {
          grids.forEach((grid) => {
            // Bug: There is an issue where if deselectAll is being called on multiple grids at the same time,
            // only the first sync  call gets initiated. This issue happens due to the missing sync call issue from bryntum
            // and is fixed in the updated version of bryntum (current is 6.0.3)
            if (grid.isSchedulerPro) {
              // adding optional chaining as on PlanView the schedule highlight feature is not enabled
              grid.features?.timeSpanHighlight?.unhighlightTimeSpans();
              grid.deselectAll();
              grid.clearEventSelection();
            } else {
              grid.deselectAll();
            }
          })
        }
      }
    }
  });
}

export const getRolesWithoutPublic = (roles) => roles.filter(
  role => !role.grants.length || role.grants.find(grant => grant.value !== GRANTS.grant_six)
);

export const handleSocketChangesOnGridStore = (grid, masterStore, getStoreData) => {
  const detacher = masterStore.on(SOCKET_CHANGES_UPDATE_NOTIFICATION, () => {
    grid.store.data = getStoreData();
  })
  const swivuEditor = grid.up(EditorBase.type, true);
  if (swivuEditor) swivuEditor.on("hide", () => detacher(), grid);
}


export const disableZoomTimeline = (schedulerInstance, isDisableMouseWheel) => {
  schedulerInstance.zoomOnMouseWheel = !isDisableMouseWheel;
  if (schedulerInstance.zoomOnMouseWheel) {
    /** Uses _zoomOnMouseWheelDetaches correctly → Prevents event reattachment and detaching issues on reload. */
    if (!schedulerInstance._zoomOnMouseWheelDetacher) {
      schedulerInstance._zoomOnMouseWheelDetacher = addTimelineZoom(schedulerInstance);
    }
  } else {
    if (schedulerInstance._zoomOnMouseWheelDetacher) {
      schedulerInstance._zoomOnMouseWheelDetacher();
      schedulerInstance._zoomOnMouseWheelDetacher = null;
    }
  }
}

export const resetSubGrids = (widgets) => {
  widgets.forEach((widget) => {
    const widgetDefaultState = widget.defaultState;
    widget.width = "auto";
    if (widget.isSchedulerPro) {
      widget.flex = "1 1 75%";
    } else {
      widget.flex = "1 1 25%";
    }
    widget.eachSubGrid((subGrid) => {
      const defaultSubGridState = widgetDefaultState.subGrids[subGrid.region];
      if ("width" in defaultSubGridState) {
        subGrid.width = defaultSubGridState.width;
      } else {
        subGrid.flex = 1;
      }
    })
    widget.columns.forEach((column) => {
      if (!column.isTimeAxisColumn) {
        const columnDefaultState = widgetDefaultState.columns.find((col) => col.id === column.id);
        if (columnDefaultState) {
          if ("width" in columnDefaultState) {
            column.width = columnDefaultState.width;
          } else {
            column.flex = 1;
          }
        }
      }
    })
  })
}

export const showSessionExpiredAlert = () => {
  MessageDialog.alert({
    title: "Session Expired",
    message: "Your session has expired! Please click OK to log in again."
  }).then(() => window.location.reload())
}

/**
 * Executes a callback with optimized bulk operation handling for stores.
 * Uses the store's performBulkOperation if available for better performance.
 *
 * @param {Array} records - Array of records to operate on
 * @param {Function} callback - The operation to perform
 */
export const performStoreBulkOperation = (records, callback) => {
  if (!records || records.length === 0) return;

  const store = records[0].stores?.[0];
  if (store?.performBulkOperation) {
    store.performBulkOperation(callback);
  } else {
    callback();
  }
};

export const showUnlinkRelatedEntityPrompt = async ({ record, relatedRecords, relatedEntityModelKey }) => {
  const records = Array.isArray(record) ? record : [record]; // Ensure record is always an array
  const hasRelatedRecords = relatedRecords?.length > 0;
  const { project, entityVisibleName, name } = records[0];
  const relatedEntityName = hasRelatedRecords ? pluralize(relatedRecords.length, relatedRecords.at(0).entityVisibleName).toLowerCase() : "N/A";
  const entityVisibleNamePlural = pluralize(records.length, entityVisibleName).toLowerCase();

  return new Promise((resolve) => {
    if (!hasRelatedRecords) {
      performStoreBulkOperation(records, () => {
        records.forEach((rec) => rec.archived = true);
      });
      return resolve(true);
    };
    const popupInstance = new Popup({
      title: `Archive ${records.length > 1 ? `${records.length} ${entityVisibleNamePlural}` : name}`,
      closable: true,
      closeAction: "destroy",
      modal: true,
      html: hasRelatedRecords
        ? `There ${relatedRecords.length > 1 ? "are" : "is a"} linked ${relatedEntityName} to ${records.length > 1 ? "these" : "this"} ${entityVisibleNamePlural}. Archiving will also archive the linked ${relatedEntityName}.`
        : `Do you really want to archive ${records.length > 1 ? "these" : "this"} ${entityVisibleNamePlural}?`,
      items: {
        disconnectRelatedEntityCheckbox: {
          type: "checkbox",
          text: `Disconnect linked ${relatedEntityName} before archiving ${records.length > 1 ? "these" : "this"} ${entityVisibleNamePlural}`,
          checked: true,
          hidden: !hasRelatedRecords
        }
      },
      bbar: {
        contentElementCls: "!px-[1em] !gap-[1em]",
        items: {
          confirmBtnText: {
            type: "button",
            text: "Archive",
            cls: "b-blue b-raised",
            onClick: async () => {
              const canDisconnectRelatedEntities = popupInstance.widgetMap.disconnectRelatedEntityCheckbox.value;

              popupInstance.mask("loading...");

              if (hasRelatedRecords && canDisconnectRelatedEntities) {
                relatedRecords.forEach((relatedRecord) => {
                  relatedRecord[relatedEntityModelKey] = null;
                });

                await project.sync(); // Sync changes before archiving
              }

              performStoreBulkOperation(records, () => {
                records.forEach((rec) => rec.archived = true);
              });
              popupInstance.unmask();
              popupInstance.close();
              resolve(true);
            }
          },
          cancelBtnText: {
            type: "button",
            text: "Cancel",
            onClick: () => {
              popupInstance.close()
              resolve(false);
            }
          }
        }
      }
    });
    popupInstance.show();
  });
};

export const canSaveView = (currentViewType) => {
  if (nonSupportedViewsForViewState.includes(currentViewType)) {
    MessageDialog.alert({
      title: "Saving & Inspection For View is Not Supported",
      message: "Saving and inspecting view of this grid is not supported."
    });
    return false;
  }

  return true;
}

/**
 * Returns a new object containing only the specified keys from the original object.
 *
 * @function pickKeysInObject
 * @param {Object} obj - The source object to pick properties from.
 * @param {string[]} keys - An array of keys to include in the returned object.
 * @returns {Object} A new object containing only the selected key-value pairs.
 *
 * @example
 * const user = { name: 'Alice', age: 30, email: 'alice@example.com' };
 * const selected = pickKeysInObject(user, ['name', 'email']);
 * // selected => { name: 'Alice', email: 'alice@example.com' }
 */
export const pickKeysInObject = (obj, keys) => {
  return keys.reduce((acc, key) => {
    if (key in obj) {
      acc[key] = obj[key];
    }
    return acc;
  }, {});
};

export function formatNameWithInitials(name) {
  if (!name || typeof name !== 'string') return '';

  const parts = name.trim().split(/\s+/);

  if (parts.length > 1) {
    const firstInitial = parts[0][0].toUpperCase();
    const restOfName = parts.slice(1).join(' ');
    return `${firstInitial}. ${restOfName}`;
  }

  return parts[0];
}

export function handleHeaderButtonMenuShowOnHover() {
  const header = document.getElementById(APP_HEADER_ID);
  let menuButtons;
  return EventHelper.on({
    element: document.body,
    mouseover: debounce(({ target }) => {
      if (!menuButtons) menuButtons = Array.from(header.querySelectorAll(`.${APP_HEADER_HOVER_MENU_BUTTON_CLS}`));
      let widget = Widget.fromElement(target);
      if (widget) {
        widget = widget.isButton ? widget : widget.up('button', true);
        if (widget && menuButtons.includes(widget.element)) {
          widget.menu.show();
        } else {
          /* We are using optional chaining here to avoid errors as there are cases where menuButtons has dom elements which don't exist in the DOM anymore therefore bryntum `fromElement` returns null. We were facing similar issue reported on sentry here:
          https://swivu.sentry.io/issues/6656514663/?environment=development&query=is%3Aunresolved%20issue.priority%3A%5Bhigh%2C%20medium%5D%20app_type%3AClient&referrer=issue-stream */
          menuButtons.forEach(button => Widget.fromElement(button)?.menu?.hide?.())
        }
      }
    }, 300)
  })
}

export const transformSwivuGridRecords = (respdata) => {
  const transformedData = {};
  if (Array.isArray(respdata)) {
    // Process each item in the data array
    respdata.forEach(item => {
      // Get the entity type from the key mapping
      const entityType = SWIVU_ASSIST_COLUMN_MAP[item.key] || item.key;
      // Add the item to the transformed data under its entity type
      transformedData[entityType] = item;
    });
  }
  // Set the transformed data in state
  window.swivuAssistanceRecord = transformedData;
}

export const geoLocationButtonUrlHelper = (url, {lat, lon}) => {
  return url.replaceAll('{lat}', lat).replaceAll('{lon}', lon);
}
export const getFormattedFileSize = (fileSize) => {
  if (fileSize) {
    const FILE_UNITS = ["bytes", "KB", "MB", "GB"];
    const FILE_CONVERSION_FACTOR = 1024;

    const unitIndex = Math.min(
      Math.floor(Math.log(fileSize) / Math.log(FILE_CONVERSION_FACTOR)),
      FILE_UNITS.length - 1
    )
    fileSize = fileSize / Math.pow(FILE_CONVERSION_FACTOR, unitIndex)
    return `${fileSize.toFixed(2)} ${FILE_UNITS[unitIndex]}`;
  }
  return "N/A";
}

/**
 * Utility function to check if a file is an image based on its filename or URL
 * @param {FormattedFile} file - The file to check URL
 * @returns {boolean} - True if the file is an image
 */
export const isImageFile = (file) => {
  // CompanyCam images are always images
  if (file.isCCImage) return true;
  const filename = file?.name;
  if (!filename || typeof filename !== "string") return false;
  const parts = filename.split(".");
  const ext = parts.length > 1 ? parts[parts.length - 1].toLowerCase() : "";
  return ["jpg", "jpeg", "png", "gif", "bmp", "svg", "webp", "ico"].includes(ext);
};

export const openSwivuAssistPopup = ({ editorHandler, swivuAssistanceConfig }) => {
  if (!editorHandler) {
    console.error("editorHandler is required to open Swivu Assist popup");
    return;
  }

  editorHandler.openEditor(
    EDITOR_KEYS.swivuAssistEditor,
    { id: 'swivu-assist' },
    { swivuAssistanceConfig },
    { by: "recordType", useExisting: true }
  );
}

export const isReplayHappen = (dataToSend) => {
  delete dataToSend.requestId;
  const lastStoreDataChanges = window.lastStoreDataChanges;
  if (window.lastStoreDataChanges && isObjDeepEqual(lastStoreDataChanges, dataToSend)) {
      console.log('[beforeSync] Replay Effect Detected', { dataToSend, lastStoreDataChanges });
      logErrorToSentry('Replay Effect Detected', { dataToSend, lastStoreDataChanges });
      window.lastStoreDataChanges = null;
      return true; // Prevent sync
    } else {
      window.lastStoreDataChanges = dataToSend;
    }
  return false;
}

export const cleanUpOrphanEventsIfAny = (dataToSend) => {
  const hasAddedEvents = Array.isArray(dataToSend?.events?.added) && dataToSend.events.added.length > 0;
  const hasAddedAssignments = Array.isArray(dataToSend?.assignments?.added) && dataToSend.assignments.added.length > 0;

  // If there are added events but no added assignments, delete the entire events property
  if (hasAddedEvents && !hasAddedAssignments) {
    const orphanEvents = dataToSend.events.added;
    delete dataToSend.events;
    console.log('[cleanUpOrphanEventsIfAny] Deleted events property (no assignments in pack):', orphanEvents);
    logErrorToSentry('Orphan Events Detected', { orphanEvents });
  }

  // If both exist, do the original orphan cleanup
  if (hasAddedEvents && hasAddedAssignments) {
    const events = dataToSend.events.added;
    const assignments = dataToSend.assignments.added;
    const assignedEventIds = new Set(assignments.map(a => a.event));
    const orphanEvents = events.filter(ev => {
      const eventId = ev.$PhantomId || ev.id;
      return !assignedEventIds.has(eventId);
    });
    if (orphanEvents.length > 0) {
      dataToSend.events.added = events.filter(ev => {
        const eventId = ev.$PhantomId || ev.id;
        return assignedEventIds.has(eventId);
      });
      console.log('[cleanUpOrphanEventsIfAny] Removed orphan events from sync payload:', orphanEvents);
    }
  }

  return dataToSend;
}

export const getDescriptionOfBoardiAsysnTask = (boardName, type) => {
  if (type === ASYNC_TASK_TYPES.BOARD_IMPORT) {
    return `Import "${boardName}"`
  } else if (type === ASYNC_TASK_TYPES.BOARD_EXPORT) {
    return `Export "${boardName}"`
  } else if (type === ASYNC_TASK_TYPES.BOARD_DUPLICATION) {
    return `Create "${boardName}"`
  } else if (type === ASYNC_TASK_TYPES.DEMO_BOARD_CREATION) {
    return `Create Test Drive "${boardName}"`
  }
}

export const classNameHelper = (...inputs) => {
  const classes = [];

  for (const input of inputs) {
    if (!input) continue;

    if (typeof input === 'string') {
      classes.push(input);
    } else if (Array.isArray(input)) {
      classes.push(...input.filter(Boolean));
    } else if (typeof input === 'object') {
      for (const [key, value] of Object.entries(input)) {
        if (value) {
          classes.push(key);
        }
      }
    }
  }

  return classes.join(' ');
};

export const getBryntumRecordAsObject = (record) => {
  return Object.entries(record.toJSON()).reduce((acc, [key, value]) => {
    const modelDefinition = record.getFieldDefinitionFromDataSource(key);
    if (modelDefinition.type === "date" && value) {
      acc[modelDefinition.name] = new Date(value)
    } else {
      acc[modelDefinition.name] = value;
    }
    return acc;
  }, {});
}

export const openNewSpotIncentiveEditor = ({ project, editorHandler, otherParams = {} }) => {
  const newSpotIncentive = project.spotIncentiveStore.createRecord();
  editorHandler.openEditor(EDITOR_KEYS.spotIncentiveEditor, newSpotIncentive, { spotIncentive: newSpotIncentive, ...otherParams });
}

export const calculateTimeSpentByUserOnTask = ({ taskId, boardUserId, project }) => {
  const { eventStore } = project;
  const events = eventStore.query((eventRecord) => eventRecord.taskId === taskId && eventRecord.resource && eventRecord.resource.eligibleBoardUsers.includes(boardUserId), true);
  if (events.length === 0) return 0;
  const timeSpent = events.reduce((sum, event) => sum + DateHelper.diff(event.startDate, event.endDate, "d"), 0);
  return timeSpent;
}

/**
 * @description Get entity icon component based on entity type
 * @param {string} type - Entity type from ENTITY_TYPES
 * @param {string} iconClassName - Optional icon class name
 * @returns {JSX.Element} Icon component
 */
export const getEntityIcon = (type, iconClassName = "h-4 w-4") => {
  switch (type) {
    case ENTITY_TYPES.TASK:
    case "Task":
      return <CheckSquare className={iconClassName} />;
    case ENTITY_TYPES.RESOURCE:
    case "Resource":
      return <Users className={iconClassName} />;
    case ENTITY_TYPES.CLIENT:
    case "Client":
      return <Building2 className={iconClassName} />;
    case ENTITY_TYPES.USER:
    case "User":
      return <UserCircle className={iconClassName} />;
    default:
      return <Link2 className={iconClassName} />;
  }
}
